if (!customElements.get('product-card')) {
  customElements.define(
    'product-card',
    class ProductCard extends HTMLElement {
      constructor() {
        super();
        this.init();
      }

      init() {
        // Selectors
        this.radios = this.querySelectorAll('input[type="radio"]');
        this.regiosItem = this.querySelector('[data-regios-dopp-generic-product-id]');
        this.short_description = this.querySelector('.boost-sd__product-short-description');
        if (
          this.short_description &&
          this.short_description.hasAttribute('data-short-description') &&
          this.short_description?.dataset?.shortDescription !== ''
        ) {
          const data = JSON.parse(this.short_description.dataset.shortDescription);
          if (data && data?.children?.[0]?.children?.length > 0) {
            const shortDescription = data?.children?.[0]?.children?.map((child) => child?.value).join('');
            this.short_description.innerHTML = shortDescription;
          }
        }

        this.product = {};
        if (
          this.querySelector('[data-product-js]') &&
          this.querySelector('[data-product-js]').innerHTML?.trim() !== ''
        ) {
          const productJson = JSON.parse(this.querySelector('[data-product-js]').innerHTML);
          this.product = this.validateProductObject(productJson);
        }
        this.selectedVariant = this.getVariantFromId(this.dataset.variantId);

        this.variantDetails = {};
        if (
          this.querySelector('[data-variant-details-js]') &&
          this.querySelector('[data-variant-details-js]').innerHTML?.trim() !== ''
        ) {
          const variantDetailsJson = JSON.parse(this.querySelector('[data-variant-details-js]').innerHTML);
          this.variantDetails = this.validateProductObject(variantDetailsJson);
        }

        // Base variant price first; market pricing overrides it for non-USD markets.
        this.updateVariantPrice();
        this.updateMarketPricing();
        this.labelPrices();
        if (this.selectedVariant?.id) {
          if (window.RegiosDOPP?.api?.v0) {
            this.updateRegiosDiscount(this.selectedVariant.id);
          } else {
            window.addEventListener('regios-dopp:api-initialized', () => {
              this.updateRegiosDiscount(this.selectedVariant.id);
            });
          }
        }

        // Events
        this.radio_events = [];
        this.radios.forEach((radio) => {
          const handler = this.updateSelection.bind(this);
          radio.addEventListener('click', handler);
          this.radio_events.push({ radio, handler });
        });
        this.content_updated_handler = this.contentUpdated.bind(this);
        this.addEventListener('content-updated', this.content_updated_handler);
        this.addEventListener('click', this.handleClick.bind(this));
      }

      handleClick(e) {
        if (
          e.target.closest('.boost-sd__product-swatch-more') ||
          e.target.classList.contains('boost-sd__product-swatch-more')
        ) {
          const link = this.querySelector('a.boost-sd__product-link');
          if (link) {
            link.click();
          }
        }
        e.stopImmediatePropagation();
        e.stopPropagation();
      }

      connectedCallback() {
        this.destroy();
        this.init();
      }

      disconnectedCallback() {
        this.destroy();
      }

      destroy() {
        this.radio_events.forEach(({ radio, handler }) => {
          radio.removeEventListener('click', handler);
        });
        this.removeEventListener('content-updated', this.content_updated_handler);
      }

      contentUpdated() {
        this.destroy();
        this.init();
      }

      updateDisplay(activeRadio) {
        const label = activeRadio.nextElementSibling;
        const variantImgUrl = activeRadio.dataset.variantImage;
        const variantUrl = activeRadio.dataset.variantUrl;
        const price = label.querySelector('.tile-option-price .price');
        const productId = activeRadio.id && activeRadio.id.indexOf('-') > -1 ? activeRadio.id.split('-')[0] : null;
        let variantId = null;
        const selectedOption = `${activeRadio.dataset.swatchType}:${activeRadio.value}`;
        this.selectedVariant = this.getSelectedVariant(selectedOption);
        if (variantImgUrl) {
          const newImagePath = variantImgUrl.split('?')[0];
          const img = this.querySelector('.card__media .media img');

          const srcQuery = img.src.includes('?') ? '?' + img.src.split('?')[1] : '';
          img.src = `${newImagePath}${srcQuery}`;

          if (img.srcset) {
            const updatedSrcset = img.srcset
              .split(',')
              .map((set) => {
                const [urlPart, size] = set.trim().split(' ');
                const query = urlPart.split('?')[1] || '';
                return `${newImagePath}?${query} ${size}`;
              })
              .join(', ');
            img.srcset = updatedSrcset;
          }
        }

        if (variantUrl) {
          // Read off a data attribute, then written back into link hrefs and a
          // regex replacement - only accept a plain positive integer id.
          variantId = sanitizeShopifyId(new URLSearchParams(variantUrl.split('?')[1]).get('variant'));
        } else if (this.selectedVariant) {
          variantId = sanitizeShopifyId(this.selectedVariant.id);
        }
        this.updateProductUrlWithVariant(variantId);
        this.updateVariantPrice();
        this.updateMarketPricing();

        if (price) {
          if (this.querySelector('.card-information .price')) {
            this.querySelector('.card-information .price').innerHTML = price.innerHTML;
          }
          if (this.querySelector('.card__information > .price')) {
            this.querySelector('.card__information > .price').innerHTML = price.innerHTML;
          }
          this.labelPrices();
          // Refresh regios discount on tile
          if (productId != null && variantId != null) {
            this.querySelector('[data-variant-id][data-product-id]')?.setAttribute('data-variant-id', variantId);
            regiosCollectionPageVariantChange(productId, variantId, this.regios_collection_key);
          }
        }

        const badge = activeRadio.dataset.variantBadge;
        const badgeEl = this.querySelector('.card__content .product-badge:not(.product-badge-regios)');
        if (badgeEl) {
          if (badge) {
            badgeEl.innerHTML = badge;
            badgeEl.classList.forEach((cls) => {
              if (cls.startsWith('product-badge-')) {
                badgeEl.classList.remove(cls);
              }
            });
            badgeEl.classList.add(`product-badge-${badge.toLowerCase().replace(/\s+/g, '-')}`);
          } else {
            badgeEl.innerHTML = '';
          }
        }
      }

      updateRegiosDiscount(variantId) {
        if (!window.RegiosDOPP?.api?.v0 || !variantId) return;
        const api = window.RegiosDOPP.api.v0;
        const regiosItem = this.querySelector('[data-regios-tile-data]');
        if (regiosItem && regiosItem.dataset.regiosTileData && regiosItem.dataset.regiosTileData !== '') {
          const regiosTileData = JSON.parse(regiosItem.dataset.regiosTileData);
          regiosItem.dataset.variantId = variantId;
          if (regiosTileData) {
            regiosTileData.variantId = variantId;
            regiosItem.dataset.regiosTileData = JSON.stringify(regiosTileData);
          }
          api.themeInterop.updateOnPagePrices(regiosItem, 'COLLECTION_PAGE', regiosTileData);
          api.calculateDiscountedPrices(regiosTileData).then(({ badge }) => {
            if (badge?.html && regiosItem.querySelector('.product-badge.product-badge-regios')) {
              regiosItem.querySelector('.product-badge.product-badge-regios').innerHTML = badge.html;
            }
          });
        }
      }

      updateSelection(e) {
        const activeRadio = e.target;
        this.updateDisplay(activeRadio);
      }

      updateRegiosDiscount(variantId) {
        if (!window.RegiosDOPP?.api?.v0 || !variantId) return;
        const api = window.RegiosDOPP.api.v0;
        const regiosItem = this.querySelector('[data-regios-tile-data]');
        if (regiosItem && regiosItem.dataset.regiosTileData && regiosItem.dataset.regiosTileData !== '') {
          const regiosTileData = JSON.parse(regiosItem.dataset.regiosTileData);
          regiosItem.dataset.variantId = variantId;
          if (regiosTileData) {
            regiosTileData.variantId = variantId;
            regiosItem.dataset.regiosTileData = JSON.stringify(regiosTileData);
          }
          api.themeInterop.updateOnPagePrices(regiosItem, 'COLLECTION_PAGE', regiosTileData);
          api.calculateDiscountedPrices(regiosTileData).then(({ badge }) => {
            if (badge?.html && regiosItem.querySelector('.product-badge.product-badge-regios')) {
              regiosItem.querySelector('.product-badge.product-badge-regios').innerHTML = badge.html;
            }
          });
        }
      }

      validateProductObject(product) {
        if (typeof product !== 'object') {
          throw new TypeError(product + ' is not an object.');
        }

        return product;
      }

      getSelectedVariant(option) {
        if (!this.product || !this?.product?.variants || !option) return null;
        return this.product.variants.find(
          (variant) => variant.original_merged_options?.includes(option) || variant.merged_options?.includes(option)
        );
      }

      getVariantFromId(id) {
        if (!this.product || !this.product.variants || !id) return null;
        return this.product.variants.find((variant) => variant.id === parseInt(id)) || null;
      }

      getSelectedMarketVariant() {
        if (!this.variantDetails || !this.variantDetails.picks || !this.selectedVariant) return this.selectedVariant;
        const selectedMarketVariant = this.variantDetails.picks.find(
          (pick) => parseInt(pick.variant.id) === parseInt(this.selectedVariant.id)
        );
        return selectedMarketVariant ? selectedMarketVariant.variant : this.selectedVariant;
      }

      updateProductUrlWithVariant(variantId) {
        if (!variantId) return;
        const targetedAttributes = ['href', 'data-product-url', 'data-url'];
        this.querySelectorAll('.variant-updated-url, .boost-sd__product-link').forEach((el) => {
          const variantUrl = this.getUrlWithVariant(el.href, variantId);
          targetedAttributes.forEach((attr) => {
            if (el.hasAttribute(attr)) {
              el.setAttribute(attr, variantUrl);
            }
          });
        });
      }

      getUrlWithVariant(url, id) {
        if (/variant=/.test(url)) {
          return url.replace(/(variant=)[^&]+/, '$1' + id);
        } else if (/\?/.test(url)) {
          return url.concat('&variant=').concat(id);
        }

        return url.concat('?variant=').concat(id);
      }

      updateMarketPricing() {
        if (!window.Shopify?.activeCurrency?.iso_code) return;
        const country = Shopify?.country || 'US';
        const activeCurrencyCode = window.Shopify?.activeCurrency?.iso_code || 'USD';
        this.selectedMarketVariant = this.getSelectedMarketVariant();
        const pricesByCountry = this?.selectedMarketVariant?.pricesByCountry?.[country] || null;
        if (activeCurrencyCode != 'USD' && pricesByCountry) {
          this.renderBoostPrice(pricesByCountry.price.amount, pricesByCountry?.compareAtPrice?.amount || null);
        }
      }

      updateVariantPrice() {
        if (!this.selectedVariant) return;
        const { price, compare_at_price } = this.selectedVariant;
        const compareAtPrice = compare_at_price && compare_at_price !== price ? compare_at_price : null;
        this.renderBoostPrice(price, compareAtPrice);
      }

      formatBoostPrice(amount) {
        const activeCurrencySymbol = window.Shopify?.activeCurrency?.symbol || '$';
        // Drop a trailing ".00" / ".0" only - never strip zeros inside cents (e.g. 19.05).
        const value = String(amount).replace(/\.0{1,2}$/, '');
        return activeCurrencySymbol === '€' ? `${value}${activeCurrencySymbol}` : `${activeCurrencySymbol}${value}`;
      }

      renderBoostPrice(amount, compareAtAmount) {
        const regularPrice = this.formatBoostPrice(amount);
        const compareAtPrice = compareAtAmount ? this.formatBoostPrice(compareAtAmount) : null;
        const priceElVisuallyHidden = this.querySelector(
          '.boost-sd__product-price-wrapper .price__regular .boost-sd__visually-hidden'
        );
        const priceEl = this.querySelector(
          '.boost-sd__product-price-wrapper .price__regular .boost-sd__format-currency'
        );
        const compareAtPriceElVisuallyHidden = this.querySelector(
          '.boost-sd__product-price-wrapper .price__sale .boost-sd__visually-hidden'
        );
        const compareAtPriceEl = this.querySelector(
          '.boost-sd__product-price-wrapper .price__sale .boost-sd__format-currency.price-item--sale'
        );
        const saleRegularPriceEl = this.querySelector(
          '.boost-sd__product-price-wrapper .price__sale .boost-sd__format-currency.price-item--regular'
        );
        const priceWrapper = priceEl ? priceEl.closest('.price') : null;
        if (priceElVisuallyHidden && priceEl) {
          priceElVisuallyHidden.textContent = `Regular price ${regularPrice}`;
          priceEl.textContent = regularPrice;
        }
        if (compareAtPriceElVisuallyHidden && compareAtPriceEl) {
          compareAtPriceElVisuallyHidden.textContent = `Now on sale for ${regularPrice}`;
          compareAtPriceEl.textContent = regularPrice;
        }
        if (saleRegularPriceEl) {
          if (compareAtPrice) {
            saleRegularPriceEl.textContent = compareAtPrice;
          }
          saleRegularPriceEl.classList.toggle('hidden', !compareAtPrice);
        }
        if (priceWrapper) {
          priceWrapper.classList.toggle('price--on-sale', compareAtAmount && compareAtAmount !== amount);
        }
      }

      // Boost repeats each amount in its visually hidden text ("Now on sale for $58.80" then "$58.80"),
      // so screen readers read every price twice. Keep only the label in front of each visible amount.
      labelPrices() {
        const priceWrapper = this.querySelector('.boost-sd__product-price-wrapper');
        if (!priceWrapper || !window.shopifyText) return;

        const regularLabel = priceWrapper.querySelector('.price__regular .boost-sd__visually-hidden');
        const [saleLabel, compareAtLabel] = priceWrapper.querySelectorAll('.price__sale .boost-sd__visually-hidden');
        if (regularLabel) regularLabel.textContent = shopifyText.regularPrice;
        if (saleLabel) saleLabel.textContent = shopifyText.salePrice;
        if (compareAtLabel) compareAtLabel.textContent = shopifyText.regularPrice;

        // The price isn't focusable, so keyboard users tabbing between cards never hear it.
        // Describe the card's product links with the price so it's announced on focus.
        if (!priceWrapper.id) {
          ProductCard.priceIdCount = (ProductCard.priceIdCount || 0) + 1;
          priceWrapper.id = `ProductCardPrice-${ProductCard.priceIdCount}`;
        }
        this.querySelectorAll('a.boost-sd__product-link').forEach((link) => {
          link.setAttribute('aria-describedby', priceWrapper.id);
        });
      }

      isJsonString(str) {
        try {
          JSON.parse(str);
        } catch (e) {
          return false;
        }
        return true;
      }
    }
  );
}
